import React, { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import {
    Row, Col, Card, CardBody, Button, Label, Table, Badge, Form,
    Pagination, PaginationItem, PaginationLink
} from "reactstrap";
import {
    FiDownload, FiCalendar, FiRefreshCcw,
    FiShoppingCart, FiPercent, FiTrendingDown, FiCheckCircle, FiSearch, FiFileText
} from "react-icons/fi";
import Select from "react-select";
import { useDispatch } from "react-redux";
import { loadingTrue, loadingFalse } from "../../Reducer/loaderSlice";
import Handler from "../../Helpers/Handler";
import { toast } from "react-toastify";
import { getCookie } from "../../Helpers/Utils";

// MUI Date Picker
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import dayjs from 'dayjs';

const SalesReportDashboard = () => {
    const navigate = useNavigate();
    const dispatch = useDispatch();

    const [rawResponse, setRawResponse] = useState(null);
    const [clients, setClients] = useState([]);
    const [isExporting, setIsExporting] = useState(false);
    const [pagination, setPagination] = useState({ total_pages: 1, total_records: 0 });

    const [filters, setFilters] = useState({
        filter_period: { value: "today", label: "Today" },
        from_date: dayjs(),
        to_date: dayjs(),
        client: null,
        page: 1,
        limit: 10
    });

    const filterOptions = [
        { value: "today", label: "Today" },
        { value: "week", label: "Last Week" },
        { value: "month", label: "Last Month" },
        { value: "3months", label: "Last 3 Months" },
        { value: "year", label: "Full Year" },
        { value: "custom", label: "Custom Range" },
        { value: "all", label: "All Records" }
    ];

    const customSelectStyles = {
        control: (provided) => ({
            ...provided,
            backgroundColor: "#f8f9fa",
            borderRadius: "10px",
            minHeight: "56px",
            boxShadow: "none",
            border: "1px solid #dee2e6"
        }),
        menu: (provided) => ({ ...provided, zIndex: 9999 })
    };

    useEffect(() => {
        fetchInitialData();
    }, []);

    const fetchInitialData = async () => {
        const res = await Handler({ method: "GET", url: "/clients/list.php" });
        if (res?.success) {
            setClients(res.data?.map(c => ({ value: c.id, label: c.client?.name?.toUpperCase() })));
        }
        loadReport();
    };

    const loadReport = async (currentFilters = filters) => {
        dispatch(loadingTrue());
        try {
            const params = {
                filter: currentFilters.filter_period.value,
                from_date: currentFilters.from_date ? currentFilters.from_date.format('YYYY-MM-DD') : "",
                to_date: currentFilters.to_date ? currentFilters.to_date.format('YYYY-MM-DD') : "",
                client_id: currentFilters.client?.value || "",
                page: currentFilters.page,
                limit: currentFilters.limit
            };
            const queryParams = new URLSearchParams(params).toString();

            const res = await Handler({ method: "GET", url: `/report/sales.php?${queryParams}` });
            if (res?.success) {
                setRawResponse(res);
                setPagination({
                    total_pages: res.total_pages,
                    total_records: res.total_records
                });
            }
        } catch (err) {
            toast.error("Sync Failed");
        } finally {
            dispatch(loadingFalse());
        }
    };

    // Paging change handler
    const handlePageChange = (newPage) => {
        const updatedFilters = { ...filters, page: newPage };
        setFilters(updatedFilters);
        loadReport(updatedFilters);
    };

    const handlePeriodChange = (selectedOption) => {
        const type = selectedOption.value;
        let from = dayjs();
        let to = dayjs();

        if (type === 'week') from = dayjs().subtract(7, 'day');
        else if (type === 'month') from = dayjs().subtract(1, 'month');
        else if (type === '3months') from = dayjs().subtract(3, 'month');

        const newFilters = { ...filters, filter_period: selectedOption, from_date: from, to_date: to, page: 1 };
        setFilters(newFilters);
        if (type !== 'custom') loadReport(newFilters);
    };

    const handleCSVDownload = async () => {
        setIsExporting(true);
        const token = getCookie('token');
        try {
            const queryParams = new URLSearchParams({
                filter: filters.filter_period.value,
                from_date: filters.from_date.format('YYYY-MM-DD'),
                to_date: filters.to_date.format('YYYY-MM-DD'),
                client_id: filters.client?.value || ""
            }).toString();

            // const downloadUrl = `${process.env.REACT_APP_API_URL || 'http://localhost/cotton-mill'}/report/sales_export.php?${queryParams}`;
            const downloadUrl = `${process.env.REACT_APP_API_URL || 'https://muthaiah-cotton.qads.co.in/cotton-mill'}/report/sales_export.php?${queryParams}`;
            
            const response = await fetch(downloadUrl, {
                headers: { 'Authorization': `Bearer ${token}` }
            });

            if (response.ok) {
                const blob = await response.blob();
                const url = window.URL.createObjectURL(blob);
                const a = document.createElement('a');
                a.href = url;
                a.download = `sales_report_${dayjs().format('YYYYMMDD')}.csv`;
                document.body.appendChild(a);
                a.click();
                window.URL.revokeObjectURL(url);
                toast.success("Report downloaded");
            }
        } catch (err) {
            toast.error("Export Failed");
        } finally { setIsExporting(false); }
    };

    const summary = rawResponse?.summary || {};
    const reportData = rawResponse?.data || [];
    // const pagination = rawResponse?.pagination || {};

    return (
        <LocalizationProvider dateAdapter={AdapterDayjs}>
            <div className="page-container p-3 bg-light min-vh-100">
                <div className="d-md-flex justify-content-between align-items-center mb-4">
                    <div>
                        <h3 className="fw-bold m-0 text-dark">Sales Analytics</h3>
                        <p className="text-muted small m-0">In-depth tracking of revenue, taxes and adjustments.</p>
                    </div>
                    <div className="d-flex gap-2 align-items-center">
                        <div style={{ width: '180px' }}>
                            <Select
                                options={filterOptions}
                                value={filters.filter_period}
                                onChange={handlePeriodChange}
                                styles={customSelectStyles}
                            />
                        </div>
                        <Button color="white" className="shadow-sm border rounded-pill px-3" style={{ height: '56px' }} onClick={() => loadReport()}>
                            <FiRefreshCcw />
                        </Button>
                    </div>
                </div>

                {/* Filters Section */}
                <Card className="border-0 shadow-sm rounded-4 mb-4">
                    <CardBody className="p-4 bg-white">
                        <Row className="g-3 align-items-end">
                            <Col lg={filters.filter_period.value === 'custom' ? 3 : 5}>
                                <Label className="x-small fw-bold text-muted ls-1 uppercase">Filter by Client</Label>
                                <Select
                                    isClearable
                                    placeholder="All Clients"
                                    options={clients}
                                    value={filters.client}
                                    onChange={(opt) => setFilters({ ...filters, client: opt, page: 1 })}
                                    styles={customSelectStyles}
                                />
                            </Col>

                            {filters.filter_period.value === 'custom' && (
                                <>
                                    <Col lg={2}>
                                        <Label className="x-small fw-bold text-muted ls-1 uppercase">Start Date</Label>
                                        <DatePicker
                                            format="DD/MM/YYYY"
                                            value={filters.from_date}
                                            onChange={(val) => setFilters({ ...filters, from_date: val, page: 1 })}
                                            sx={{ width: '100%', '& .MuiOutlinedInput-root': { borderRadius: '10px', backgroundColor: '#f8f9fa', border: 'none', height: '56px' } }}
                                        />
                                    </Col>
                                    <Col lg={2}>
                                        <Label className="x-small fw-bold text-muted ls-1 uppercase">End Date</Label>
                                        <DatePicker
                                            format="DD/MM/YYYY"
                                            value={filters.to_date}
                                            minDate={filters.from_date}
                                            onChange={(val) => setFilters({ ...filters, to_date: val, page: 1 })}
                                            sx={{ width: '100%', '& .MuiOutlinedInput-root': { borderRadius: '10px', backgroundColor: '#f8f9fa', border: 'none', height: '56px' } }}
                                        />
                                    </Col>
                                </>
                            )}

                            <Col lg={2}>
                                <Button color="primary" block className="fw-bold shadow-sm rounded-3" style={{ height: '56px', background: '#2e4a3d', border: 'none' }} onClick={() => loadReport()}>
                                    <FiSearch className="me-1" /> Run
                                </Button>
                            </Col>

                            <Col lg={2}>
                                <Button color="success" block className="fw-bold shadow-sm rounded-3" style={{ height: '56px' }} onClick={handleCSVDownload} disabled={isExporting}>
                                    {isExporting ? '...' : <><FiDownload className="me-1" /> Export</>}
                                </Button>
                            </Col>
                        </Row>
                    </CardBody>
                </Card>

                {/* Summary Row */}
                <Row className="g-3 mb-4">
                    <Col lg={3} md={6}><StatCard title="Gross Sales" value={`₹${summary.gross_sales?.toLocaleString() || 0}`} icon={<FiShoppingCart />} color="#0d6efd" bg="#0d6efd15" /></Col>
                    <Col lg={3} md={6}><StatCard title="TDS Total" value={`₹${summary.total_tds?.toLocaleString() || 0}`} icon={<FiPercent />} color="#ffc107" bg="#ffc10715" /></Col>
                    <Col lg={3} md={6}><StatCard title="Total Credits" value={`₹${summary.total_credit_notes?.toLocaleString() || 0}`} icon={<FiTrendingDown />} color="#dc3545" bg="#dc354515" /></Col>
                    <Col lg={3} md={6}><StatCard title="Net Revenue" value={`₹${summary.net_revenue?.toLocaleString() || 0}`} icon={<FiCheckCircle />} color="#198754" bg="#19875415" /></Col>
                </Row>

                {/* Table Breakdown */}
                <Card className="border-0 shadow-sm rounded-4 overflow-hidden">
                    <div className="p-3 bg-white border-bottom d-flex justify-content-between align-items-center">
                        <h6 className="m-0 fw-bold small text-uppercase text-muted">Sales Performance Details</h6>
                        <Badge color="light" className="text-dark border px-3 rounded-pill">{pagination.total_records || 0} Invoices</Badge>
                    </div>
                    <CardBody className="p-0">
                        <Table responsive hover className="mb-0 align-middle">
                            <thead className="bg-light">
                                <tr className="text-muted x-small text-uppercase ls-1">
                                    <th className="ps-4 py-3">Invoice & Party</th>
                                    <th className="text-center">Tax & Adjustments</th>
                                    <th className="text-end pe-4">Net Realized</th>
                                </tr>
                            </thead>
                            <tbody>
                                {reportData.length > 0 ? (
                                    reportData.map((item) => (
                                        <tr key={item.invoice_id}>
                                            <td className="ps-4 py-3">
                                                <div className="fw-bold text-dark">{item.invoice_no}</div>
                                                <div className="small text-primary text-uppercase" style={{fontSize: '11px'}}>{item.client.name}</div>
                                                <small className="text-muted x-small">{dayjs(item.created_at).format('DD MMM YYYY')}</small>
                                            </td>
                                            <td className="text-center">
                                                <div className="d-flex flex-column gap-1">
                                                    <span className="x-small text-muted">TDS: <b className="text-warning">₹{item.amounts.tds?.toLocaleString()}</b></span>
                                                    <span className="x-small text-muted">CN: <b className="text-danger">₹{item.amounts.credit_note?.toLocaleString()}</b></span>
                                                </div>
                                            </td>
                                            <td className="text-end pe-4">
                                                <div className="fw-bold text-success fs-6">₹{item.amounts.net_revenue?.toLocaleString()}</div>
                                                <small className="text-muted x-small">Gross: ₹{item.amounts.gross_sale?.toLocaleString()}</small>
                                            </td>
                                        </tr>
                                    ))
                                ) : (
                                    <tr><td colSpan="3" className="py-5 text-center text-muted italic">No data records found.</td></tr>
                                )}
                            </tbody>
                        </Table>
                    </CardBody>
                </Card>

                {/* Pagination Sync from Ref */}
                {pagination.total_pages > 1 && (
                    <div className="d-flex justify-content-center mt-4">
                        <Pagination className="pagination-rounded shadow-sm">
                            <PaginationItem disabled={filters.page <= 1}>
                                <PaginationLink previous onClick={() => handlePageChange(filters.page - 1)} />
                            </PaginationItem>
                            {[...Array(pagination.total_pages)].map((_, i) => (
                                <PaginationItem active={i + 1 === filters.page} key={i}>
                                    <PaginationLink onClick={() => handlePageChange(i + 1)}>{i + 1}</PaginationLink>
                                </PaginationItem>
                            ))}
                            <PaginationItem disabled={filters.page >= pagination.total_pages}>
                                <PaginationLink next onClick={() => handlePageChange(filters.page + 1)} />
                            </PaginationItem>
                        </Pagination>
                    </div>
                )}
            </div>
        </LocalizationProvider>
    );
};

const StatCard = ({ title, value, icon, color, bg }) => (
    <Card className="border-0 shadow-sm rounded-4 h-100 bg-white border-bottom border-4" style={{ borderColor: color }}>
        <CardBody className="d-flex align-items-center gap-3 p-4">
            <div style={{ width: '50px', height: '50px', borderRadius: '14px', backgroundColor: bg, color: color, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '22px' }}>
                {icon}
            </div>
            <div>
                <p className="m-0 text-muted x-small fw-bold text-uppercase ls-1">{title}</p>
                <h4 className="m-0 fw-bold" style={{ color: '#2c3e50' }}>{value}</h4>
            </div>
        </CardBody>
    </Card>
);

export default SalesReportDashboard;